"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { Loader2 } from "lucide-react";
import { resolveLanding } from "@/lib/active-tenant";
import { useApi } from "@/hooks/use-api";
import { useBrandState } from "@/components/brand/BrandProvider";
import { hostLockFor } from "@/lib/host-brand";

export default function Home() {
  const router = useRouter();
  const { user, loading } = useAuth();
  // ADR 0418 D8 — on an org's own domain the domain decides the org (matched by slug)
  const brandState = useBrandState();
  const { data: myOrgs } = useApi<{ tenant_id: string; org_slug: string }[]>(user ? "/organizations/mine" : null);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      router.replace("/login");
      return;
    }

    // `storedOrgStatus` stays null here on purpose: checking whether a super_admin's
    // stored org still exists costs a round trip, and the dashboard layout — which
    // every dashboard page passes through — already owns that check. Doing it here
    // too would only delay the landing (ADR 0126).
    const decision = resolveLanding({
      storedTenantId: localStorage.getItem("active_tenant_id"),
      memberOrgIds: (user.org_roles || []).map((r) => r.org_id),
      isSuperAdmin: user.tms_system_role === "super_admin",
      storedOrgStatus: null,
      hostLock: hostLockFor(brandState, myOrgs),
    });

    if (decision.route === null) return;
    if (decision.clearStoredTenant) localStorage.removeItem("active_tenant_id");
    if (decision.route === "/dashboard" && decision.adoptTenantId) {
      localStorage.setItem("active_tenant_id", decision.adoptTenantId);
    }
    router.replace(decision.route);
  }, [user, loading, router, brandState, myOrgs]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--page-bg)" }}>
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--primary-ink)" }} />
    </div>
  );
}
